import { useState } from "react";
import {
  Steps,
  Input,
  Select,
  Card,
  Button,
  Badge,
  Tag,
  Typography,
  Space,
  Row,
  Col,
  Modal,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { Icon } from 'Components';

const { Title, Text } = Typography;
const { Step } = Steps;

type Template = {
  id: string | number;
  title: string;
  category: string;
  difficulty: string;
};

type DifficultyStyle = {
  bg: string;
  color: string;
};

const DIFFICULTY_COLOR: Record<string, DifficultyStyle> = {
  Beginner: { bg: "#E3F0FB", color: "#006CA3" },
  Intermediate: { bg: "#FFF4E5", color: "#FF8C00" },
  Advanced: { bg: "#FDECEA", color: "#D93025" },
};

type CampaignSetupProps = {
  onBack: () => void;
};

const TEMPLATES = [
  {
    id: 1,
    title: "Fake Login Page",
    category: "AI-Driven",
    difficulty: "Beginner",
  },
  {
    id: 2,
    title: "Booking Hotel Scam",
    category: "Hospitality-Specific",
    difficulty: "Advanced",
  },
  {
    id: 3,
    title: "Urgent CEO Request",
    category: "Core Essentials",
    difficulty: "Beginner",
  },
  {
    id: 4,
    title: "Urgent CEO Request",
    category: "AI-Driven",
    difficulty: "Beginner",
  },
  {
    id: 5,
    title: "Fake Login Page",
    category: "AI-Driven",
    difficulty: "Intermediate",
  },
  {
    id: 6,
    title: "Booking Hotel Scam",
    category: "Core Essentials",
    difficulty: "Beginner",
  },
  {
    id: 7,
    title: "Urgent CEO Request",
    category: "Hospitality-Specific",
    difficulty: "Advanced",
  },
];

type TemplateCardProps = {
  template: Template;
  selected: boolean;
  onSelect: (id: string | number | null) => void;
};

type StepPlaceholderProps = {
  step: number;
  label: React.ReactNode;
  onBack?: () => void;
  onNext: () => void;
  isLast?: boolean;
};

type Step1Props = {
  selectedTemplate: string | number | null;
  onSelect: (id: string | number | null) => void;
  onNext: () => void;
  onClose: () => void;
};

function TemplateCard({ template, selected, onSelect }: TemplateCardProps) {
  const diff = DIFFICULTY_COLOR[template.difficulty] || DIFFICULTY_COLOR.Beginner;
  return (
    <div
      onClick={() => selected ? onSelect(null) : onSelect(template.id)}
      className="cs-template-card-wrapper"
      style={{
        border: selected ? "2px solid #006CA3" : "1px solid #EAEAEB",
        boxShadow: selected
          ? "0 0 0 3px rgba(0,108,163,0.15)"
          : "0px 2px 8px rgba(0,0,0,0.10)",
      }}
    >
      <div className="cs-relative">
        <img
          src="/placeholderImage.png"
          alt={template.title}
          className="cs-image-cover"
        />
        <span
          className="cs-image-word-cover"
          style={{
            background: diff.bg,
            color: diff.color
          }}
        >
          {template.difficulty}
        </span>
        {selected && (
          <div
            className="cs-selected-checkbox"
          >
            <CheckCircleFilled className="text-white fsx-16" />
          </div>
        )}
      </div>
      <div className="cs-template-card">
        <div className="cs-template-card-title">
          {template.title}
        </div>
        <div className="cs-template-card-category">
          {template.category}
        </div>
        <button
          className="cs-template-card-button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(template.id);
          }}
          style={{
            background: selected ? "#005a87" : "#006CA3",
          }}
        >
          {selected ? "✓ Selected" : "Select"}
        </button>
      </div>

    </div>
  );
}

function StepPlaceholder({ step, label, onBack, onNext, isLast }: StepPlaceholderProps) {
  return (
    <div className="cs-steps-wrapper">
      <div className="cs-step-number">{step}</div>

      <Title level={4} className="m-0">
        {label}
      </Title>

      <Text type="secondary">Configure step {step} of 5 here.</Text>

      <div className="d-flex gap-12 mtx-24">
        {onBack && (
          <button onClick={onBack} className="cs-launch-back-button">
            Back
          </button>
        )}

        {!isLast ? (
          <button onClick={onNext} className="cs-launch-next-button">
            Next
          </button>
        ) : (
          <button onClick={onNext} className="cs-launch-campaign-button">
            Launch Campaign
          </button>
        )}
      </div>
    </div>
  );
}

function Step1({ selectedTemplate, onSelect, onNext, onClose }: Step1Props) {
  const [search, setSearch] = useState<string>("");
  const [language, setLanguage] = useState<string>("English");
  const [category, setCategory] = useState<string>("All categories");
  const [difficulty, setDifficulty] = useState<string>("All difficulties");
  const dropdownIcon = (
    <div className="cs-dropdownWrapper">
      <Icon type="campaignSetupDropdown" />
    </div>
  );
  const filtered = TEMPLATES.filter((t) => {
    const matchSearch =
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.category.toLowerCase().includes(search.toLowerCase());
    const matchCategory =
      category === "All categories" || t.category === category;
    const matchDifficulty =
      difficulty === "All difficulties" || t.difficulty === difficulty;
    return matchSearch && matchCategory && matchDifficulty;
  });

  const categories = ["All categories", ...new Set(TEMPLATES.map((t) => t.category))];
  const difficulties = ["All difficulties", "Beginner", "Intermediate", "Advanced"];

  return (
    <div className="campaign-template-list">
      <div>
        <div className="campaign-scenario">
          Scenario
        </div>
        <div className="campaign-scenario-subtitle">
          Choose the scenario for the campaign
        </div>
      </div>
      <div className="d-flex gap-24 justify-between flex-wrap align-center">
        <Input
          placeholder="Search"
          suffix={
            <Icon type="campaignSetupSearch" />
          }
          className="campaign-setup-search"
        />

        <Select
          className="ml-select campaign-setup-language-select"
          suffixIcon={dropdownIcon}
          defaultValue="English"
          options={[{ value: "English", label: "English" }, { value: "French", label: "French" }]}
        />

        <Select
          className="ml-select campaign-setup-categories-select"
          suffixIcon={dropdownIcon}
          placeholder="All categories"
          options={[{ value: "cat1", label: "Category 1" }]}
        />

        <Select
          className="ml-select campaign-setup-difficulties-select"
          suffixIcon={dropdownIcon}
          placeholder="All difficulties"
          options={[{ value: "easy", label: "Easy" }]}
        />
      </div>
      <div className="cs-template-list-header">
        Select template ({filtered.length} results)
      </div>

      {filtered.length === 0 ? (
        <div className="cs-template-empty-state">
          No templates match your filters.
        </div>
      ) : (
        <Row gutter={[20, 20]}>
          {filtered.map((t) => (
            <Col key={t.id} xs={24} sm={12} md={8} lg={6}>
              <TemplateCard
                template={t}
                selected={selectedTemplate === t.id}
                onSelect={onSelect}
              />
            </Col>
          ))}
        </Row>
      )}
      <div className="d-flex justify-end mtx-8">
        <button
          disabled={!selectedTemplate}
          onClick={onNext}
          style={{
            background: selectedTemplate ? "#006CA3" : "#EAEAEB",
            color: selectedTemplate ? "#fff" : "#999",
            border: "none",
            borderRadius: 8,
            padding: "10px 32px",
            fontWeight: 600,
            fontSize: 15,
            cursor: selectedTemplate ? "pointer" : "not-allowed",
            transition: "all 0.18s",
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default function CampaignSetup({ onBack }: CampaignSetupProps) {
  const [open, setOpen] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<string | number | null>(null);

  const STEPS = [
    "Scenario",
    <>Test<br />Configuration</>,
    <>Full Campaign<br />Recipients</>,
    <>Delivery &<br /> Schedule</>,
    <>Review & <br /> Start Test</>,
  ];


  const handleClose = () => onBack();
  const handleNext = () => setCurrentStep((s) => Math.min(s + 1, 4));
  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  return (
    <div className="phishingManager">
      <div className="phishingManagerStep1Wrapper"
      >
        <div className="w-full mw-1200">
          <div className="d-flex align-start justify-between mb-40">
            <div className="mw-880">
              <Title level={3} className="campaign-title">
                Campaign setup
              </Title>
              <Text className="campaign-subtitle">
                Follow the steps to create a new campaign
              </Text>
            </div>
            <button
              onClick={handleClose}
              className="campaign-setup-close-wrapper"
              title="Close"
            >
              <Icon type="campaignSetupClose" />
            </button>
          </div>
          <div className="phishing-step1">
            <div className="mw-900 mx-auto">
              <Steps
                current={currentStep}
                className="mw-900 mx-auto d-flex gap-70"
                size="small" labelPlacement="vertical"
              >
                {STEPS.map((label, i) => (
                  <Step
                    status={
                      i < currentStep
                        ? "finish"
                        : i === currentStep
                          ? "process"
                          : "wait"
                    }
                    className="d-flex justify-center"
                    style={{ cursor: i <= currentStep ? "pointer" : "default" }}
                    title={label} />))}
              </Steps>
            </div>
          </div>
          {currentStep === 0 && (
            <Step1
              selectedTemplate={selectedTemplate}
              onSelect={setSelectedTemplate}
              onNext={handleNext}
              onClose={handleClose}
            />
          )}
          {currentStep === 1 && (
            <StepPlaceholder
              step={2}
              label={
                <>
                  Test
                  <div>Configuration</div>
                </>
              }
              onBack={handleBack}
              onNext={handleNext}
            />
          )}
          {currentStep === 2 && (
            <StepPlaceholder
              step={3}
              label={<>Full Campaign <br />Recipients</>}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}
          {currentStep === 3 && (
            <StepPlaceholder
              step={4}
              label={<>Delivery & <br />Schedule</>}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}
          {currentStep === 4 && (
            <StepPlaceholder
              step={5}
              label={
                <>
                  Review & <span className="d-block">Start Test</span>
                </>
              }
              onBack={handleBack}
              onNext={() => {
                alert("Campaign launched successfully!");
                setCurrentStep(0);
                setSelectedTemplate(null);
              }}
              isLast
            />
          )}
        </div>
      </div>
    </div>
  );
}
